import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  CONTEXTUAL_BG_BASE,
  getContextualThemeForModule,
  pathSegmentToThemeKey,
} from "../lib/contextualTheme";

function mulberry32(seed: number) {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

type FireflySpec = {
  leftPct: number;
  topPct: number;
  sizePx: number;
  durS: number;
  delayS: number;
  x1: string;
  y1: string;
  x2: string;
  y2: string;
  o0: string;
  o1: string;
  o2: string;
};

export function SongLearnCanvasBackdrop({
  children,
  className = "",
  /** `lite`: menos luciérnagas y animaciones más baratas (gimnasio, etc.). */
  ambient = "full",
}: {
  children: React.ReactNode;
  className?: string;
  ambient?: "full" | "lite";
}) {
  const { pathname } = useLocation();
  const theme = useMemo(() => {
    const seg = pathname.split("/").filter(Boolean)[0] ?? "";
    const key = pathSegmentToThemeKey(seg);
    return getContextualThemeForModule(key);
  }, [pathname]);

  const fireflyCount = ambient === "lite" ? 10 : 34;

  const fireflies = useMemo(() => {
    const rnd = mulberry32(ambient === "lite" ? 0x60f11e7 : 0xcafebabe);
    const out: FireflySpec[] = [];
    for (let i = 0; i < fireflyCount; i++) {
      out.push({
        leftPct: rnd() * 100,
        topPct: rnd() * 100,
        sizePx: 2 + Math.floor(rnd() * 2.5),
        durS: 12 + rnd() * 24,
        delayS: -rnd() * 28,
        x1: `${(rnd() * 2 - 1) * 16}vw`,
        y1: `${(rnd() * 2 - 1) * 10}vh`,
        x2: `${(rnd() * 2 - 1) * 13}vw`,
        y2: `${(rnd() * 2 - 1) * 12}vh`,
        o0: String(0.2 + rnd() * 0.18),
        o1: String(0.55 + rnd() * 0.28),
        o2: String(0.28 + rnd() * 0.22),
      });
    }
    return out;
  }, [ambient, fireflyCount]);

  const waveFill = theme.tint2;
  const waveFillHi = theme.tint;

  return (
    <div
      className={`relative flex min-h-0 w-full max-w-full flex-col overflow-x-hidden overflow-y-auto transition-all duration-700 ${className}`}
    >
      <div
        className="pointer-events-none absolute inset-0 z-0 transition-all duration-700"
        style={{
          backgroundColor: CONTEXTUAL_BG_BASE,
          transitionProperty: "background-color, opacity, filter",
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 z-0 transition-all duration-700"
        style={{
          background: `radial-gradient(ellipse 120% 70% at 50% -15%, ${theme.tint}, transparent 55%)`,
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 z-0 transition-all duration-700"
        style={{
          background: `radial-gradient(ellipse 90% 55% at 100% 100%, ${theme.tint2}, transparent 50%)`,
          opacity: 0.85,
        }}
        aria-hidden
      />
      <style>{`
        @keyframes learn-firefly-drift {
          0% { transform: translate3d(0, 0, 0); opacity: var(--ff-o0); }
          33% { transform: translate3d(var(--ff-x1), var(--ff-y1), 0); opacity: var(--ff-o1); }
          66% { transform: translate3d(var(--ff-x2), var(--ff-y2), 0); opacity: var(--ff-o2); }
          100% { transform: translate3d(0, 0, 0); opacity: var(--ff-o0); }
        }
        @keyframes learn-firefly-drift-lite {
          0%, 100% { transform: translate3d(0, 0, 0); }
          50% { transform: translate3d(var(--ff-x1), var(--ff-y1), 0); }
        }
        .learn-firefly {
          contain: layout style;
        }
        @media (prefers-reduced-motion: reduce) {
          .learn-firefly { animation: none !important; opacity: 0.35; }
        }
      `}</style>
      <svg
        className="pointer-events-none absolute left-0 top-0 z-0 w-full transition-opacity duration-700"
        style={{ height: "46%", opacity: 0.07 }}
        viewBox="0 0 1200 220"
        preserveAspectRatio="none"
        aria-hidden
      >
        <path fill={waveFillHi} d="M0,130 Q280,70 560,100 T1120,85 L1200,95 L1200,220 L0,220 Z" />
        <path fill={waveFill} d="M0,155 Q360,110 720,135 T1200,115 L1200,220 L0,220 Z" />
      </svg>
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden>
        {fireflies.map((f, i) => (
          <span
            key={i}
            className="learn-firefly absolute rounded-full"
            style={
              {
                left: `${f.leftPct}%`,
                top: `${f.topPct}%`,
                width: f.sizePx,
                height: f.sizePx,
                background: "rgba(255,255,255,0.88)",
                boxShadow:
                  ambient === "lite"
                    ? "0 0 6px 1px rgba(255,255,255,0.2)"
                    : "0 0 4px 1px rgba(255,255,255,0.35), 0 0 14px 3px rgba(255,255,255,0.18)",
                opacity: ambient === "lite" ? 0.42 : undefined,
                "--ff-x1": f.x1,
                "--ff-y1": f.y1,
                "--ff-x2": f.x2,
                "--ff-y2": f.y2,
                "--ff-o0": f.o0,
                "--ff-o1": f.o1,
                "--ff-o2": f.o2,
                animation:
                  ambient === "lite"
                    ? `learn-firefly-drift-lite ${f.durS * 1.4}s ease-in-out infinite`
                    : `learn-firefly-drift ${f.durS}s ease-in-out infinite`,
                animationDelay: `${f.delayS}s`,
              } as React.CSSProperties
            }
          />
        ))}
      </div>
      <div className="relative z-[1] box-border flex min-h-0 w-full min-w-0 flex-1 flex-col px-4 pb-5 pt-0 sm:px-7 md:px-10 md:pb-6">
        {children}
      </div>
    </div>
  );
}
